import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Banknote } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/api";
import PriceDetails from "@/components/PriceDetails";

const CartPage = () => {
  const navigate = useNavigate();
  const {
    items,
    updateQty,
    removeItem,
    totalMrp,
    discount,
    delivery,
    payable,
    totalQty,
  } = useCart();

  if (items.length === 0) {
    return (
      <main className="max-w-[900px] mx-auto px-4 py-10" data-testid="empty-cart">
        <div className="bg-white rounded-sm fk-shadow py-16 flex flex-col items-center gap-4">
          <ShoppingBag className="w-16 h-16 text-[#C62828]" />
          <p className="text-lg font-head font-semibold text-[#212121]">
            Your cart is empty
          </p>
          <p className="text-sm text-[#878787]">Add items to it now.</p>
          <Link
            to="/"
            className="bg-[#C62828] text-white font-semibold px-8 py-2.5 rounded-sm hover:bg-[#A01F1F]"
            data-testid="shop-now-btn"
          >
            Shop Now
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-[1400px] mx-auto px-2 sm:px-4 py-3" data-testid="cart-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-8 bg-white rounded-sm fk-shadow">
          <p className="font-head text-lg font-bold px-4 py-3 border-b border-[#f0f0f0]">
            My Cart ({totalQty})
          </p>
          {items.map((it) => (
            <div
              key={it.id}
              className="flex gap-4 p-4 border-b border-[#f0f0f0]"
              data-testid={`cart-item-${it.id}`}
            >
              <Link to={`/product/${it.id}`} className="w-20 h-20 shrink-0 flex items-center justify-center">
                <img
                  src={it.image_url}
                  alt={it.name}
                  className="max-h-full max-w-full object-contain mix-blend-multiply"
                />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${it.id}`} className="text-sm text-[#212121] line-clamp-2 hover:text-[#C62828]">
                  {it.name}
                </Link>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="font-semibold text-[#212121]">{formatPrice(it.price)}</span>
                  <span className="text-xs text-[#878787] line-through">{formatPrice(it.mrp)}</span>
                  <span className="text-xs text-[#388e3c] font-semibold">
                    {Math.round(((it.mrp - it.price) * 100) / it.mrp)}% off
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQty(it.id, it.qty - 1)}
                      className="w-7 h-7 rounded-full border border-[#c2c2c2] flex items-center justify-center text-[#333] disabled:opacity-40"
                      disabled={it.qty <= 1}
                      data-testid={`qty-dec-${it.id}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-9 text-center text-sm font-medium border border-[#c2c2c2] rounded-sm py-0.5" data-testid={`qty-${it.id}`}>
                      {it.qty}
                    </span>
                    <button
                      onClick={() => updateQty(it.id, it.qty + 1)}
                      className="w-7 h-7 rounded-full border border-[#c2c2c2] flex items-center justify-center text-[#333]"
                      data-testid={`qty-inc-${it.id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(it.id)}
                    className="flex items-center gap-1 text-sm font-semibold text-[#212121] hover:text-[#fb641b]"
                    data-testid={`remove-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-end p-4 sticky bottom-0 bg-white">
            <button
              onClick={() => navigate("/checkout")}
              className="bg-[#fb641b] text-white font-semibold px-10 py-3 rounded-sm hover:brightness-95 shadow-md"
              data-testid="place-order-btn"
            >
              PLACE ORDER
            </button>
          </div>
        </div>

        <div className="lg:col-span-4 lg:sticky lg:top-32">
          <PriceDetails
            totalMrp={totalMrp}
            discount={discount}
            delivery={delivery}
            payable={payable}
            totalQty={totalQty}
          />
          <div className="mt-3 bg-white rounded-sm fk-shadow px-4 py-3 flex items-center gap-3" data-testid="cod-available-note">
            <Banknote className="w-5 h-5 text-[#388e3c] shrink-0" />
            <p className="text-sm text-[#212121]">
              <b>COD Available</b> — pay cash on delivery (+12% COD fee)
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default CartPage;
